/**
 * ArtworkPublicBucketField — per-location editor for the dedicated PUBLIC
 * artwork bucket (BLU-662), one instance per location row in
 * LocationSettingsSection.
 *
 * WHY it exists: `artwork_public_bucket` / `artwork_public_region` shipped as
 * SQL-only production_settings keys (migration 20260718130000). A dynamic
 * setting writable only via SQL is an unfinished feature (CLAUDE.md §10), and
 * the failure mode is silent broken publishes — a mistyped or non-public bucket
 * makes standard-catalogue artwork uploads land somewhere print-time fetch can
 * never read. So this field is verify-BEFORE-save: Save is disabled for any
 * non-empty changed value until `verifyArtworkPublicBucket` confirms the bucket
 * exists, is public-read, and is on the print resolver's host allowlist.
 * CLEARING (emptying a previously-saved value) needs no verification.
 *
 * BLU-1476, Tencent: the bucket is the location's Singapore bucket, private
 * as a whole, with only its `public-artwork` folder public. Verify also proves
 * the rest of the bucket refuses an anonymous read (`privateOutsidePrefix`),
 * and Save stays locked until it does.
 *
 * Self-contained per-location state (draft bucket/region, verify result, which
 * value that result attests to) keeps LocationSettingsSection readable and
 * mirrors how the section already renders per-location controls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldCheck, Cloud } from 'lucide-react';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { toast } from 'sonner';
import { TENCENT_COS_REGION_OPTIONS } from '../tencent-cos-regions';
import { VerifyFacet } from './VerifyFacet';

type LocationSetting = RouterOutputs['productionSettings']['getLocationSettings'][number];
type VerifyResult = RouterOutputs['productionSettings']['verifyArtworkPublicBucket'];

interface ArtworkPublicBucketFieldProps {
  location: LocationSetting;
  /** Called after a successful save/clear so the parent can refetch the list. */
  onSaved: () => void;
}

/** This field is not translated yet: its facets keep their English screen-reader outcome. */
const FACET_SR = { srPassedLabel: 'passed', srFailedLabel: 'failed' } as const;

export function ArtworkPublicBucketField({ location, onSaved }: ArtworkPublicBucketFieldProps) {
  const isTencent = location.printFileSource === 'tencent_cos';
  const backend: 'gcs' | 'tencent_cos' = isTencent ? 'tencent_cos' : 'gcs';

  const savedBucket = location.artworkPublicBucket ?? '';
  const savedRegion = location.artworkPublicRegion ?? '';

  const [bucketDraft, setBucketDraft] = useState(savedBucket);
  const [regionDraft, setRegionDraft] = useState(savedRegion);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  // The exact (bucket, region) the current verifyResult attests to. Editing the
  // field after a pass makes this stop matching, which re-locks Save until the
  // new value is re-verified.
  const [verifiedFor, setVerifiedFor] = useState<{ bucket: string; region: string } | null>(null);
  // Live snapshot of the (bucket, region) currently in the draft, refreshed every
  // render. The verify probe can take up to 8s (PROBE_TIMEOUT_MS); the input stays
  // editable during that window, so its async onSuccess/onError read this ref to
  // decide whether the resolved result still describes what's in the field. Reading
  // draft STATE inside those callbacks would be stale-closure-prone; the ref is not.
  const verifyTargetRef = useRef<{ bucket: string; region: string }>({ bucket: '', region: '' });

  const inputId = `artwork-bucket-${location.id}`;
  const regionId = `artwork-region-${location.id}`;

  // Re-sync drafts + clear the verify state whenever the PERSISTED value changes
  // (i.e. after a successful save + refetch). Keyed on the saved strings so
  // in-flight typing is never clobbered mid-edit.
  useEffect(() => {
    setBucketDraft(savedBucket);
    setRegionDraft(savedRegion);
    setVerifyResult(null);
    setVerifiedFor(null);
  }, [savedBucket, savedRegion]);

  // A probe result only describes the value it was fired for. The bucket/region
  // input is now disabled for the up-to-8s duration of the probe (MUST-FIX,
  // BLU-662 review — an editable field mid-verify let an operator land in a
  // contradictory verified-state-for-a-different-value), so this can no
  // longer happen from direct typing. It's still reachable if the PERSISTED
  // value changes underneath the in-flight probe (another tab/session saves,
  // triggering the savedBucket/savedRegion resync effect above) — in that
  // case committing the result would paint green facets for a name no longer
  // in the input while Save stays locked (verifiedFor !== draft) with no
  // explanation (showSaveGate is suppressed once a result exists). Drop stale
  // results instead; the amber Save-gate helper then re-appears for the
  // current value.
  const isStaleProbe = (variables: { bucket: string; region?: string }) =>
    variables.bucket !== verifyTargetRef.current.bucket ||
    (variables.region ?? '') !== verifyTargetRef.current.region;

  const verifyMutation = trpc.productionSettings.verifyArtworkPublicBucket.useMutation({
    onSuccess: (result, variables) => {
      if (isStaleProbe(variables)) return;
      setVerifyResult(result);
      setVerifiedFor({ bucket: variables.bucket, region: variables.region ?? '' });
    },
    onError: (error, variables) => {
      // Swallow a stale error too — toasting "could not verify" for an abandoned
      // value would be as confusing as painting a stale pass.
      if (isStaleProbe(variables)) return;
      setVerifyResult(null);
      setVerifiedFor(null);
      toast.error(`Could not verify bucket: ${error.message}`);
    },
  });

  const saveMutation = trpc.productionSettings.setLocationArtworkPublicBucket.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(variables.bucket === null ? 'Artwork bucket cleared' : 'Artwork bucket saved');
      onSaved();
    },
    onError: (error) => {
      toast.error(`Failed to save artwork bucket: ${error.message}`);
    },
  });

  const trimmedBucket = bucketDraft.trim();
  const isEmpty = trimmedBucket === '';
  const region = isTencent ? regionDraft : '';

  // Keep the in-flight-probe staleness check pointed at the live draft.
  verifyTargetRef.current = { bucket: trimmedBucket, region };

  const isDirty = trimmedBucket !== savedBucket || (isTencent && region !== savedRegion);

  const verifyPassed =
    verifyResult != null &&
    verifyResult.bucketReachable &&
    verifyResult.publiclyReadable &&
    verifyResult.allowlisted &&
    // Tencent: the rest of the bucket (the upload holding area included) must stay private.
    (!isTencent || verifyResult.privateOutsidePrefix === true) &&
    verifiedFor != null &&
    verifiedFor.bucket === trimmedBucket &&
    verifiedFor.region === region;

  const canVerify = !isEmpty && (!isTencent || region !== '') && !verifyMutation.isPending && !saveMutation.isPending;

  // MUST-FIX (BLU-662 review): the inputs must be locked for the duration of
  // an in-flight verify, not just an in-flight save. Otherwise the operator
  // can edit the bucket/region mid-probe and the eventual result paints
  // facets for a value that's no longer in the field.
  const fieldsLocked = saveMutation.isPending || verifyMutation.isPending;

  // Clearing a previously-saved value is a safe no-verify escape hatch.
  const isClearing = isEmpty && savedBucket !== '';
  const canSave = !saveMutation.isPending && (isClearing || (!isEmpty && isDirty && verifyPassed));

  const clearVerify = () => {
    setVerifyResult(null);
    setVerifiedFor(null);
  };

  const handleVerify = () => {
    if (!canVerify) return;
    verifyMutation.mutate({
      locationId: location.id,
      bucket: trimmedBucket,
      backend,
      ...(isTencent ? { region } : {}),
    });
  };

  const handleSave = () => {
    if (!canSave) return;
    if (isClearing) {
      saveMutation.mutate({ locationId: location.id, bucket: null, region: null });
    } else {
      saveMutation.mutate({
        locationId: location.id,
        bucket: trimmedBucket,
        region: isTencent ? region : null,
      });
    }
  };

  // The exact ARTWORK_HOST_ALLOWLIST entry an operator should add — NOT just the
  // host. The entry is always "<host>/<first path segment>/", and never the
  // bare host. The print resolver scopes allowlist entries by host +
  // PATH-PREFIX (F-A1, artwork-fetch.ts `isAllowedHost`).
  //   GCS is PATH-STYLE (storage.googleapis.com/<bucket>/<key>): the first
  //   segment is the bucket. A bare `storage.googleapis.com` would match EVERY
  //   bucket on the shared GCS host (a cross-tenant read surface).
  //   Tencent COS is VIRTUAL-HOSTED (<bucket>.cos.ap-singapore.myqcloud.com):
  //   the first segment is `public-artwork`. The bucket is the floor's PRIVATE
  //   ingest bucket that also holds print files, so a bare host would let
  //   print fetch read all of it; only the public-artwork folder is opened.
  // No first segment: no suggestion (never fall back to the bare host).
  const allowlistSuggestion = useMemo(() => {
    if (!verifyResult?.durableUrlExample) return null;
    let url: URL;
    try {
      url = new URL(verifyResult.durableUrlExample);
    } catch {
      return null;
    }
    const firstSegment = url.pathname.split('/').filter(Boolean)[0];
    return firstSegment ? `${url.hostname}/${firstSegment}/` : null;
  }, [verifyResult?.durableUrlExample]);

  // Suppressed once a verify result exists (pass OR fail) — the result box
  // below owns post-verify messaging, so this stays "you haven't verified
  // yet" guidance only, never contradicting a rendered pass/fail facet.
  const showSaveGate = !isEmpty && isDirty && !verifyPassed && !verifyMutation.isPending && !verifyResult;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Cloud className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <span className="text-xs font-medium">Artwork public bucket</span>
      </div>
      {isTencent ? (
        <p className="max-w-prose text-xs text-muted-foreground">
          Use your Singapore bucket. Artwork goes in the public-artwork folder,
          and only that folder is public. Tencent COS uses the "name-appid"
          bucket form (e.g. mz-artwork-1250000000). Verify writes a small test
          file there with this organisation's Tencent credentials, then deletes it.
          Never personalised/custom-order artwork.
        </p>
      ) : (
        <p className="max-w-prose text-xs text-muted-foreground">
          This location's own dedicated public-read bucket for standard catalogue
          variant artwork. Never the private print bucket, and never
          personalised/custom-order artwork. GCS uses a plain bucket name (e.g.
          mz-public-artwork). The bucket must already exist in this location's own
          cloud account, public-read, uniform bucket-level access, before you
          verify it here; it is not created for you.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={inputId} className="text-xs text-muted-foreground/90">
            Bucket name
          </Label>
          <Input
            id={inputId}
            name={`artwork-public-bucket-${location.id}`}
            value={bucketDraft}
            onChange={(e) => {
              setBucketDraft(e.target.value);
              clearVerify();
            }}
            placeholder={isTencent ? 'mz-artwork-1250000000' : 'mz-public-artwork'}
            className="h-8 w-[260px] max-w-full text-xs"
            autoComplete="off"
            spellCheck={false}
            disabled={fieldsLocked}
          />
        </div>

        {isTencent && (
          <div className="space-y-1">
            <Label htmlFor={regionId} className="text-xs text-muted-foreground/90">
              Region
            </Label>
            <Select
              value={region || undefined}
              onValueChange={(v) => {
                setRegionDraft(v);
                clearVerify();
              }}
              disabled={fieldsLocked}
            >
              <SelectTrigger
                id={regionId}
                className="h-8 w-[200px] text-xs"
                aria-label={`Artwork bucket region for ${location.name}`}
              >
                <SelectValue placeholder="Choose a region" />
              </SelectTrigger>
              <SelectContent>
                {TENCENT_COS_REGION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          className="h-8 gap-1.5 px-3 text-xs"
          onClick={handleVerify}
          disabled={!canVerify}
        >
          {verifyMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Verify
        </Button>

        <Button
          type="button"
          className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {isClearing ? 'Clear' : 'Save'}
        </Button>
      </div>

      {/* Why Save is gated — amber helper, mirrors DESIGN.md §13.9's required-but-unmet pattern. */}
      {showSaveGate && (
        <p className="text-xs text-amber-700">
          {isTencent && region === ''
            ? 'Choose a region, then verify the bucket before saving.'
            : 'Verify the bucket before saving.'}
        </p>
      )}

      {/* Verify outcome — three facets + failure / allowlist guidance.
          The polite live region is ALWAYS mounted and only its CONTENT swaps:
          NVDA/JAWS reliably announce content inserted into a region that
          already exists in the DOM, but routinely miss content mounted at the
          same instant as its aria-live container (DESIGN.md §16 requires async
          status to be announced). Each VerifyFacet carries an sr-only "passed"/
          "failed" suffix, so the outcome — and therefore *why* Save stays
          disabled — is spoken even though the amber Save-gate helper is
          suppressed once a result exists. */}
      <div role="status" aria-live="polite">
        {verifyResult && (
          <div className="space-y-1.5 rounded-md border p-3">
            <VerifyFacet ok={verifyResult.bucketReachable} label="Bucket exists" {...FACET_SR} />
            <VerifyFacet
              ok={verifyResult.publiclyReadable}
              label={isTencent ? 'public-artwork folder is public' : 'Publicly readable'}
              {...FACET_SR}
            />
            {isTencent && (
              <VerifyFacet
                ok={verifyResult.privateOutsidePrefix === true}
                label="Private outside public-artwork"
                {...FACET_SR}
              />
            )}
            <VerifyFacet ok={verifyResult.allowlisted} label="Allowlisted for print fetch" {...FACET_SR} />
            {verifyPassed && (
              <p className="pt-0.5 text-xs text-emerald-700">Verified. Save to apply.</p>
            )}
            {verifyResult.failReason && (
              <p className="pt-0.5 text-xs text-destructive">{verifyResult.failReason}</p>
            )}
            {!verifyResult.allowlisted && allowlistSuggestion && (
              <p className="pt-0.5 text-xs text-amber-700">
                Ask PrintRun to allowlist {allowlistSuggestion} for artwork fetching.
              </p>
            )}
            {verifyResult.durableUrlExample && (
              <p className="break-all pt-0.5 text-[11px] text-muted-foreground">
                Example URL: {verifyResult.durableUrlExample}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
